/**
 * Inline cell editing for <g-table> — the pure half.
 *
 * Opt-in per column: nothing here runs unless a ColumnDef says `editable`.
 * The element owns the interaction (which cell is open, focus, save state);
 * this file owns what an editor looks like for a column and how typed text
 * becomes a value, so both can be pinned in node without a DOM.
 *
 * The editor is inferred from the column's `render`, because the column
 * already says what kind of value it holds — a currency column edits as a
 * number, a badge column edits as a pick from its own colorMap. `editor`
 * overrides the inference when the two genuinely differ.
 */
import { escapeHtml } from '../util/actions.js';
import { calendarDate } from '../util/dates.js';

export const EDITORS = new Set(['text', 'textarea', 'number', 'currency', 'percent', 'date', 'datetime', 'select', 'boolean']);

// An actions column has no value at all, so it never edits.
const NOT_EDITABLE = new Set(['actions']);

const BY_RENDER = {
  currency: 'currency',
  number: 'number',
  percent: 'percent',
  date: 'date',
  datetime: 'datetime',
  boolean: 'boolean',
  badge: 'select',
};

/** Which editor a column edits with, or null when it cannot be edited. */
export function editorFor(col) {
  if (!col || !col.field || NOT_EDITABLE.has(col.render)) return null;
  // A linked record edits as a pick from the records the host lists in
  // `options` ({ value: record, label }), and not at all without them.
  if (col.render === 'ref-link') return optionsFor(col).length ? 'select' : null;
  if (EDITORS.has(col.editor)) return col.editor;
  if (col.render === 'badge' && !optionsFor(col).length) return 'text';
  return BY_RENDER[col.render] || (Array.isArray(col.options) ? 'select' : 'text');
}

/**
 * Whether this cell is editable right now. `editable` may be a boolean or a
 * `(row) => boolean`, so "spent is editable until the budget is closed" needs
 * no second column.
 */
export function isEditable(row, col, readonly = false) {
  if (readonly || !col || !col.editable || !editorFor(col)) return false;
  if (typeof col.editable === 'function') {
    try { return !!col.editable(row); } catch { return false; }
  }
  return col.editable === true;
}

/** [{ value, label }] for a select editor: `options`, else the badge colorMap keys. */
export function optionsFor(col) {
  const src = Array.isArray(col.options) ? col.options
    : (col.colorMap && typeof col.colorMap === 'object' ? Object.keys(col.colorMap) : []);
  return src
    .map((o) => (o && typeof o === 'object')
      ? { value: o.value, label: o.label ?? String(o.value) }
      : { value: o, label: String(o) })
    .filter((o) => o.value !== undefined && o.value !== null);
}

const pad = (n) => String(n).padStart(2, '0');

// A linked record is the same pick whether it is held populated or as its id.
const keyOf = (v) => (v && typeof v === 'object' && v._id != null ? String(v._id) : String(v));

/** The text an editor opens with for a stored value. */
export function draftFor(v, col) {
  const kind = editorFor(col);
  if (v == null) return '';
  switch (kind) {
    case 'number':
    case 'currency': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (!Number.isFinite(n)) return String(v);
      // The same rounding the cell displays, so opening a ragged float does
      // not show the user 13216.000000000002.
      return String(Number.isInteger(n) ? n : Math.round(n * 100) / 100);
    }
    case 'percent': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      // Edited in the units it is shown in (93 for 93%), stored as a 0–1 ratio.
      return Number.isFinite(n) ? String(Math.round(n * 100 * 10000) / 10000) : '';
    }
    case 'date': {
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
      // "Jan 12, 2027" must open as 2027-01-12 — read as local midnight it
      // opened a day early east of UTC, and saving wrote that day back.
      const d = calendarDate(v);
      return d ? d.toISOString().slice(0, 10) : '';
    }
    case 'datetime': {
      const d = new Date(v);
      if (isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    case 'select': {
      const opts = optionsFor(col);
      let hit = opts.findIndex((o) => keyOf(o.value) === keyOf(v));
      // A host that flattens linked records for display (mapRow) hands the
      // cell the record's name rather than the record.
      if (hit < 0 && col.render === 'ref-link' && typeof v === 'string') hit = opts.findIndex((o) => o.label === v);
      return hit >= 0 ? String(hit) : '';
    }
    default:
      return typeof v === 'object' ? '' : String(v);
  }
}

/**
 * Typed text → a stored value, or an error the editor shows in place.
 *
 * Returns `{ value }` or `{ error }`. Empty input stores null (not '' or 0),
 * which is what an unset field already looks like everywhere else.
 */
export function parseDraft(raw, col, row) {
  const kind = editorFor(col);
  const text = String(raw ?? '').trim();
  let value;
  if (text === '') {
    if (col.required) return { error: 'Required' };
    value = null;
  } else {
    switch (kind) {
      case 'number':
      case 'currency':
      case 'percent': {
        // Typed by people: tolerate "1,200", "$80" and "93%".
        const n = Number(text.replace(/[\s,$€£¥%]/g, ''));
        if (!Number.isFinite(n)) return { error: 'Enter a number' };
        value = kind === 'percent' ? n / 100 : n;
        const shown = kind === 'percent' ? n : value;
        if (Number.isFinite(col.min) && shown < col.min) return { error: `Must be at least ${col.min}` };
        if (Number.isFinite(col.max) && shown > col.max) return { error: `Must be at most ${col.max}` };
        break;
      }
      case 'date':
        if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return { error: 'Enter a date' };
        value = text;
        break;
      case 'datetime': {
        const d = new Date(text);
        if (isNaN(d.getTime())) return { error: 'Enter a date and time' };
        value = d.toISOString();
        break;
      }
      case 'select': {
        const opt = optionsFor(col)[Number(text)];
        if (!opt) return { error: 'Pick an option' };
        value = opt.value;
        break;
      }
      default:
        value = text;
    }
  }
  if (typeof col.validate === 'function') {
    let verdict;
    try { verdict = col.validate(value, row); } catch (err) { verdict = err && err.message ? err.message : 'Invalid value'; }
    if (typeof verdict === 'string' && verdict) return { error: verdict };
    if (verdict === false) return { error: 'Invalid value' };
  }
  return { value };
}

/** Same stored value? Loose on purpose: null/'' and 5/"5" are not edits. */
export function sameValue(a, b) {
  const blank = (x) => x == null || x === '';
  if (blank(a) || blank(b)) return blank(a) && blank(b);
  return keyOf(a) === keyOf(b);
}

/** Truthy/falsy the way the boolean renderer reads it — so a toggle flips what the cell shows. */
export function toggled(v) {
  const truthy = v === true || v === 1 || v === '1' || v === 'true' || v === 'yes';
  return !truthy;
}

/**
 * Someone else changed the cell that is open here. Untouched, the editor
 * simply takes the new value. Typed into, the typing stays — but Escape now
 * returns to the new value, and `remote` says what it is, so saving over it
 * is a choice and not an accident (QA B05-05: the open cell kept the old
 * number, and Enter silently overwrote the other user's change).
 *
 * @param {{draft: string, initial: string}} session  the open edit
 * @returns {{draft: string, initial: string, remote: string|null}} `remote` is
 *   the new value as the editor shows it, or null when the draft took it
 */
export function reconcileOpenEdit(session, value, col) {
  const fresh = draftFor(value, col);
  if (session.draft === session.initial) return { draft: fresh, initial: fresh, remote: null };
  let shown = fresh;
  if (editorFor(col) === 'select') shown = fresh === '' ? '' : (optionsFor(col)[Number(fresh)]?.label ?? '');
  return { draft: session.draft, initial: fresh, remote: shown === '' ? '—' : shown };
}

const INPUT_SIZE = { xs: 'input-xs', sm: 'input-xs', md: 'input-sm', lg: 'input-md' };
const SELECT_SIZE = { xs: 'select-xs', sm: 'select-xs', md: 'select-sm', lg: 'select-md' };

/** Editor markup for an open cell. `draft` and `error` are user text, escaped. */
export function renderEditor(col, { draft = '', error = null, size = 'sm', label = '', remote = null } = {}) {
  const kind = editorFor(col);
  const invalid = error ? ' aria-invalid="true"' : '';
  const aria = ` aria-label="${escapeHtml(label)}"${invalid}`;
  const err = error
    ? `<div data-g-cell-err class="text-error text-xs text-left whitespace-normal" role="alert">${escapeHtml(error)}</div>`
    : remote != null
      ? `<div data-g-cell-err data-g-cell-note class="text-xs text-left whitespace-normal" role="status">Changed to ${escapeHtml(remote)} by someone else — Enter saves yours, Esc takes theirs</div>`
      : '';
  if (kind === 'select') {
    const opts = optionsFor(col).map((o, i) =>
      `<option value="${i}"${String(i) === draft ? ' selected' : ''}>${escapeHtml(o.label)}</option>`).join('');
    const blank = col.required ? '' : `<option value=""${draft === '' ? ' selected' : ''}>—</option>`;
    return `<select data-g-cell-input class="select ${SELECT_SIZE[size] || 'select-xs'} w-full${error ? ' select-error' : ''}"${aria}>${blank}${opts}</select>${err}`;
  }
  if (kind === 'textarea') {
    return `<textarea data-g-cell-input rows="3" class="textarea textarea-xs w-full${error ? ' textarea-error' : ''}"${aria}>${escapeHtml(draft)}</textarea>${err}`;
  }
  const type = kind === 'date' ? 'date' : kind === 'datetime' ? 'datetime-local' : 'text';
  // Numbers use a text input with a decimal keypad: type="number" rejects
  // "1,200" silently (value becomes "") and changes the value on a stray
  // scroll-wheel, both worse than parsing the text ourselves.
  const mode = (kind === 'number' || kind === 'currency' || kind === 'percent') ? ' inputmode="decimal"' : '';
  return `<input data-g-cell-input type="${type}"${mode} value="${escapeHtml(draft)}" class="input ${INPUT_SIZE[size] || 'input-xs'} w-full${error ? ' input-error' : ''}"${aria} autocomplete="off">${err}`;
}
